import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

const copyToClipboard = (text) => {
  try {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  } catch {
    toast.error('Copy failed');
  }
};

function AccountCard({ account, onChanged }) {
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [subscribingAll, setSubscribingAll] = useState(false);
  const [subscribingId, setSubscribingId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [days, setDays] = useState(30);
  const [claimingId, setClaimingId] = useState(null);
  const [testingConn, setTestingConn] = useState(false);

  const manageLeadsPages = (account.pages || []).filter((p) => p.has_manage_leads);
  const foreignLeadPages = (account.foreign_pages || []).filter((p) => p.has_manage_leads);

  const claimPage = async (pageId) => {
    setClaimingId(pageId);
    try {
      await apiClient.post(`/meta-lead-forms/accounts/${account.key}/claim-page/${pageId}`);
      toast.success('Page moved to this account ✓ — now click Subscribe leadgen');
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not move page');
    } finally {
      setClaimingId(null);
    }
  };
  const bf = account.backfill_status;
  const backfillRunning = !!bf?.running;

  const startBackfill = async () => {
    try {
      await apiClient.post(`/meta-lead-forms/accounts/${account.key}/backfill?days=${days}`);
      toast.success('Import started — leads will appear shortly');
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start import');
    }
  };

  // Poll for progress while a backfill is running
  useEffect(() => {
    if (!backfillRunning) return;
    const t = setInterval(() => { onChanged(); }, 4000);
    return () => clearInterval(t);
  }, [backfillRunning, onChanged]);

  const removeAccount = async () => {
    if (!window.confirm(`Remove "${account.label}" from the CRM? Its leads stay, but it will no longer receive new ones until re-added.`)) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/meta-lead-forms/accounts/${account.key}`);
      toast.success(`Removed "${account.label}"`);
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove account');
    } finally {
      setDeleting(false);
    }
  };

  const saveToken = async () => {
    const t = token.replace(/\s+/g, '');
    if (!t) { toast.error('Paste the access token first'); return; }
    setSaving(true);
    try {
      const r = await apiClient.post(`/meta-lead-forms/accounts/${account.key}/token`, { token: t });
      const skipped = r.data.pages_skipped || [];
      toast.success(`Token verified — ${r.data.verified_account_name} · ${r.data.pages_synced} Page(s) synced`);
      if (skipped.length) toast.info(`Skipped ${skipped.length} Page(s) already owned by another account: ${skipped.join(', ')}`);
      setToken('');
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Token rejected by Meta');
    } finally {
      setSaving(false);
    }
  };

  const syncPages = async () => {
    setSyncing(true);
    try {
      const r = await apiClient.post(`/meta-lead-forms/accounts/${account.key}/sync-pages`);
      toast.success(`Synced ${r.data.synced} Page(s)`);
      if ((r.data.skipped || []).length) toast.info(`Skipped ${r.data.skipped.length} Page(s) owned by another account: ${r.data.skipped.join(', ')}`);
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const subscribePage = async (pageId) => {
    setSubscribingId(pageId);
    try {
      await apiClient.post(`/meta-lead-forms/accounts/${account.key}/subscribe-page/${pageId}`);
      toast.success('Page subscribed to leadgen ✓');
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Subscribe failed');
    } finally {
      setSubscribingId(null);
    }
  };

  const subscribeAll = async () => {
    setSubscribingAll(true);
    try {
      const r = await apiClient.post(`/meta-lead-forms/accounts/${account.key}/subscribe-all`);
      toast.success(`Subscribed ${r.data.subscribed}/${r.data.total} Page(s)`);
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Subscribe-all failed');
    } finally {
      setSubscribingAll(false);
    }
  };

  const testConnection = async () => {
    setTestingConn(true);
    try {
      const r = await apiClient.post(`/meta-lead-forms/accounts/${account.key}/test-connection`);
      if (r.data?.success) {
        let expiryMsg = '';
        if (r.data.expires_in_days === null) {
          expiryMsg = ' · token never expires';
        } else if (typeof r.data.expires_in_days === 'number') {
          expiryMsg = r.data.expires_in_days <= 7
            ? ` · ⚠️ token expires in ${r.data.expires_in_days}d`
            : ` · token valid ${r.data.expires_in_days}d`;
        }
        const msg = `Connected ✓ — ${r.data.name} · ${r.data.pages} Page(s), ${r.data.subscribed_pages} subscribed${expiryMsg}`;
        if (typeof r.data.expires_in_days === 'number' && r.data.expires_in_days <= 7) {
          toast.warning(msg);
        } else {
          toast.success(msg);
        }
      } else {
        toast.error(`Connection failed: ${r.data?.error || 'Unknown error'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test connection failed');
    } finally {
      setTestingConn(false);
    }
  };

  return (
    <div className="border rounded-xl p-4 space-y-4" data-testid={`meta-account-${account.key}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
            <i className="fab fa-facebook text-blue-600"></i>
          </div>
          <div>
            <div className="font-semibold text-slate-800">{account.label}</div>
            {account.verified_account_name && (
              <div className="text-xs text-slate-500">Connected as {account.verified_account_name}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={account.token_configured ? 'bg-green-100 text-green-800 border-green-300' : 'bg-yellow-100 text-yellow-800 border-yellow-300'}
            data-testid={`meta-account-status-${account.key}`}
          >
            {account.token_configured ? 'Configured · verify connection' : 'Token Needed'}
          </Badge>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-slate-400 hover:text-red-600"
            onClick={removeAccount}
            disabled={deleting}
            title={`Remove ${account.label}`}
            data-testid={`meta-account-delete-${account.key}`}
          >
            <i className="fas fa-trash text-xs"></i>
          </Button>
        </div>
      </div>

      {/* Auto-sync status (runs every 5 min) */}
      {account.token_configured && (
        <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5" data-testid={`meta-autosync-${account.key}`}>
          <i className="fas fa-rotate text-emerald-600"></i>
          <span>
            Auto-sync is ON (every 5 min).
            {account.last_auto_sync_at
              ? ` Last run ${new Date(account.last_auto_sync_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` +
                (account.auto_sync_status ? ` — ${account.auto_sync_status.new || 0} new lead(s).` : '.')
              : ' Waiting for first run…'}
          </span>
        </div>
      )}

      {/* Token input */}
      <div className="bg-slate-50 border rounded-lg p-3">
        <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold">System User Token</Label>
        <p className="text-xs text-slate-500 mt-0.5 mb-2">
          Paste a never-expiring Business System User token (scopes: leads_retrieval, pages_show_list, pages_manage_metadata, pages_read_engagement, business_management).
        </p>
        <div className="flex gap-2">
          <Input
            type="password"
            placeholder="Paste EAA... token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid={`meta-account-token-input-${account.key}`}
          />
          <Button onClick={saveToken} disabled={saving || !token.trim()} data-testid={`meta-account-save-token-${account.key}`}>
            {saving ? 'Verifying…' : 'Save & Sync'}
          </Button>
        </div>
      </div>

      {/* Pages */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-sm">Lead Pages</h4>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={testConnection} disabled={testingConn || !account.token_configured} data-testid={`meta-account-test-${account.key}`}>
              {testingConn ? 'Testing…' : <><i className="fas fa-plug mr-1" />Test Connection</>}
            </Button>
            <Button size="sm" variant="outline" onClick={syncPages} disabled={syncing || !account.token_configured} data-testid={`meta-account-sync-${account.key}`}>
              {syncing ? 'Syncing…' : 'Sync Pages'}
            </Button>
            {manageLeadsPages.length > 0 && (
              <Button size="sm" onClick={subscribeAll} disabled={subscribingAll} data-testid={`meta-account-subscribe-all-${account.key}`}>
                {subscribingAll ? 'Subscribing…' : 'Subscribe All'}
              </Button>
            )}
          </div>
        </div>
        {manageLeadsPages.length ? (
          <div className="border rounded-lg divide-y">
            {manageLeadsPages.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`meta-account-page-${account.key}-${p.id}`}>
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-slate-500">ID: {p.id}</div>
                </div>
                {p.subscribed ? (
                  <Badge className="bg-green-100 text-green-800 border-green-300" variant="outline">Subscribed ✓</Badge>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => subscribePage(p.id)} disabled={subscribingId === p.id} data-testid={`meta-account-subscribe-${account.key}-${p.id}`}>
                    {subscribingId === p.id ? 'Subscribing…' : 'Subscribe leadgen'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-500 border rounded-lg p-3">
            {account.pages?.length
              ? 'None of the connected Pages have the MANAGE_LEADS permission. Ensure your token has lead access, then Sync Pages.'
              : (account.token_configured ? 'No Pages found. Click Sync Pages.' : 'Paste a token above to connect Pages.')}
          </div>
        )}
      </div>

      {/* Pages this token can see but that belong to another account */}
      {foreignLeadPages.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid={`meta-foreign-pages-${account.key}`}>
          <h4 className="font-medium text-sm text-blue-900">Lead Pages held by another account</h4>
          <p className="text-xs text-blue-700 mb-2">Your token can access these, but they're currently assigned elsewhere. Move one here if it belongs to {account.label}.</p>
          <div className="border rounded-lg divide-y bg-white">
            {foreignLeadPages.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`meta-foreign-page-${account.key}-${p.id}`}>
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-slate-500">ID: {p.id}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => claimPage(p.id)} disabled={claimingId === p.id} data-testid={`meta-claim-page-${account.key}-${p.id}`}>
                  {claimingId === p.id ? 'Moving…' : 'Move to this account'}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Import existing (historical) leads */}
      {manageLeadsPages.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`meta-backfill-${account.key}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h4 className="font-medium text-sm text-amber-900">Import existing leads</h4>
              <p className="text-xs text-amber-700">Pull leads submitted before you subscribed. Safe to re-run — duplicates are skipped automatically.</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                className="text-sm border rounded-md px-2 py-1.5 bg-white"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                disabled={backfillRunning}
                data-testid={`meta-backfill-range-${account.key}`}
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={0}>All available</option>
              </select>
              <Button size="sm" onClick={startBackfill} disabled={backfillRunning} data-testid={`meta-backfill-btn-${account.key}`}>
                {backfillRunning ? 'Importing…' : 'Import'}
              </Button>
            </div>
          </div>
          {bf && (
            <div className="mt-2 text-xs text-amber-900 bg-white border border-amber-200 rounded px-2 py-1.5" data-testid={`meta-backfill-status-${account.key}`}>
              {bf.running ? (
                <span><i className="fas fa-spinner fa-spin mr-1"></i>Importing… {bf.created || 0} new · {bf.duplicates || 0} duplicates · {bf.forms_scanned || 0} forms scanned</span>
              ) : (
                <span><i className="fas fa-check-circle text-green-600 mr-1"></i>Done — imported {bf.created || 0} new lead(s), {bf.duplicates || 0} already existed{bf.errors ? `, ${bf.errors} error(s)` : ''} (scanned {bf.forms_scanned || 0} form(s)).</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MetaLeadFormsSettings({ inline = false } = {}) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [recent, setRecent] = useState([]);
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const [accRes, recRes] = await Promise.all([
        apiClient.get('/meta-lead-forms/accounts'),
        apiClient.get('/meta-lead-forms/recent-leads?limit=10'),
      ]);
      setConfig(accRes.data);
      setRecent(recRes.data?.events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Meta Lead Forms config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addAccount = async () => {
    const key = newKey.trim();
    const label = newLabel.trim();
    if (!key || !label) { toast.error('Enter both a key and a display label'); return; }
    setAdding(true);
    try {
      await apiClient.post('/meta-lead-forms/accounts', { key, label });
      toast.success(`Account "${label}" added`);
      setNewKey(''); setNewLabel('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add account');
    } finally {
      setAdding(false);
    }
  };

  const body = (
    <>
      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <>
          {/* Shared webhook config */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Callback URL</Label>
              <div className="flex gap-2 mt-1">
                <Input value={config?.webhook_url || ''} readOnly data-testid="meta-lf-webhook-url" />
                <Button variant="outline" onClick={() => copyToClipboard(config?.webhook_url || '')} data-testid="meta-lf-copy-url-btn">Copy</Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Meta App Dashboard → Webhooks → Page → Callback URL (one URL serves all accounts)</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Verify Token</Label>
              <div className="flex gap-2 mt-1">
                <Input value={config?.verify_token || ''} readOnly data-testid="meta-lf-verify-token" />
                <Button variant="outline" onClick={() => copyToClipboard(config?.verify_token || '')} data-testid="meta-lf-copy-token-btn">Copy</Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Paste beside the Callback URL in Meta</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div className="border rounded-lg p-3">
              <div className="text-xs text-slate-500">App Secret</div>
              <div className="mt-1 font-medium">{config?.app_secret_configured ? '✓ Configured' : '— Missing'}</div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-xs text-slate-500">Graph API Version</div>
              <div className="mt-1 font-medium">{config?.graph_api_version || '—'}</div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-xs text-slate-500">Events Received</div>
              <div className="mt-1 font-medium">{config?.events_received ?? 0}</div>
            </div>
          </div>

          {/* Accounts */}
          <div className="space-y-4">
            <h4 className="font-semibold text-slate-700">Connected Meta Accounts</h4>
            {(config?.accounts || []).map((acc) => (
              <AccountCard key={acc.key} account={acc} onChanged={load} />
            ))}
          </div>

          {/* Add account */}
          <div className="border rounded-lg p-3 bg-slate-50">
            <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Add another account</Label>
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <Input placeholder="key (e.g. newbiz)" value={newKey} onChange={(e) => setNewKey(e.target.value)} data-testid="meta-add-account-key" />
              <Input placeholder="Display label (e.g. New Biz Meta)" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} data-testid="meta-add-account-label" />
              <Button onClick={addAccount} disabled={adding} data-testid="meta-add-account-btn">{adding ? 'Adding…' : 'Add'}</Button>
            </div>
          </div>

          {/* Recent events */}
          <div>
            <h4 className="font-medium mb-2">Recent Lead Events</h4>
            {recent.length ? (
              <div className="border rounded-lg divide-y max-h-72 overflow-auto">
                {recent.map((ev) => (
                  <div key={ev.leadgen_id} className="px-3 py-2 text-sm" data-testid={`meta-lf-event-${ev.leadgen_id}`}>
                    <div className="flex justify-between">
                      <span className="font-mono text-xs">leadgen_id: {ev.leadgen_id}</span>
                      <span className="text-xs text-slate-500">{ev.processed_at}</span>
                    </div>
                    <div className="text-xs text-slate-600">
                      source: {ev.meta_account_label || '—'} · page: {ev.page_id || '—'} · ad: {ev.lead_data?.ad_name || ev.lead_data?.ad_id || '—'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500 border rounded-lg p-3">
                No events yet. Once a Page is subscribed, new lead form submissions appear here.
              </div>
            )}
          </div>
        </>
      )}
    </>
  );

  if (inline) {
    return <div data-testid="meta-lead-forms-card" className="space-y-5">{body}</div>;
  }

  return (
    <Card data-testid="meta-lead-forms-card">
      <CardHeader>
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <i className="fab fa-facebook text-blue-600 text-xl"></i>
          </div>
          <div>
            <CardTitle>Meta Lead Forms</CardTitle>
            <CardDescription>Connect multiple Meta accounts (Patrick's, Gradbud, …) — leads flow in automatically with round-robin assignment.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">{body}</CardContent>
    </Card>
  );
}
